import type { CodeEntry, CodeFamily } from '@/types';

/**
 * Compact authoring form for status and error codes. Merged into GLOSSARY
 * as category `code` entries — do not import this module from the UI.
 */
export const FAMILY_LABELS: Record<CodeFamily, string> = {
  'iso-tx-status': 'ISO 20022 transaction status',
  'iso-status-reason': 'ISO 20022 status reason',
  'bg-error': 'Berlin Group error',
  'stet-error': 'STET error',
  'ukob-error': 'UK Open Banking error',
  'sca-status': 'SCA status',
  'consent-status': 'Consent status',
  'scheme-status': 'Scheme status (A2A overlay)',
};

/**
 * The registry answers one question: I got this code back — what now?
 * `action` is the useful column and the reason this exists rather than a link
 * to the ISO external code list.
 */
export const CODES: CodeEntry[] = [
  // ── ISO 20022 transaction status ────────────────────────────────────────
  {
    code: 'RCVD',
    family: 'iso-tx-status',
    name: 'Received',
    description: 'The instruction reached the bank and passed nothing more than a syntax check.',
    severity: 'info',
    action: 'Wait. Authorisation has not happened yet.',
  },
  {
    code: 'ACTC',
    family: 'iso-tx-status',
    name: 'AcceptedTechnicalValidation',
    description: 'Syntactically and semantically valid. Authorisation is complete but the payment has not been booked.',
    severity: 'pending',
    action: 'Keep polling. Do not tell the customer the money has moved.',
  },
  {
    code: 'ACCP',
    family: 'iso-tx-status',
    name: 'AcceptedCustomerProfile',
    description: 'Passed customer profile checks — account exists, is open, the debtor is allowed to send.',
    severity: 'pending',
    action: 'Keep polling.',
  },
  {
    code: 'ACSP',
    family: 'iso-tx-status',
    name: 'AcceptedSettlementInProgress',
    description: 'Handed to clearing. Committed on the debtor side but not yet settled with the creditor bank.',
    severity: 'pending',
    action: 'Safe to show as "sent". Not safe to show as "received".',
  },
  {
    code: 'ACSC',
    family: 'iso-tx-status',
    name: 'AcceptedSettlementCompleted',
    description: 'Settled. The creditor account has been credited.',
    severity: 'success',
    action: 'Terminal and positive. Release goods, close the order.',
  },
  {
    code: 'ACWC',
    family: 'iso-tx-status',
    name: 'AcceptedWithChange',
    description: 'Accepted, but the bank altered something — commonly the execution date pushed to the next business day.',
    severity: 'pending',
    action: 'Read the returned resource and reconcile against what you sent.',
  },
  {
    code: 'PDNG',
    family: 'iso-tx-status',
    name: 'Pending',
    description: 'Blocked on something: value date not reached, manual review, or a clearing answer that never came.',
    severity: 'pending',
    action: 'Never retry the initiation. Duplicates start here.',
  },
  {
    code: 'RJCT',
    family: 'iso-tx-status',
    name: 'Rejected',
    description: 'Refused. Look at StsRsnInf for the reason — the status alone tells you nothing actionable.',
    severity: 'error',
    action: 'Terminal. Get the reason code before deciding whether a retry is even legitimate.',
  },
  {
    code: 'CANC',
    family: 'iso-tx-status',
    name: 'Cancelled',
    description: 'Cancelled by the PSU or the bank before execution.',
    severity: 'error',
    action: 'Terminal. Nothing to retry.',
  },
  {
    code: 'PART',
    family: 'iso-tx-status',
    name: 'PartiallyAccepted',
    description: 'Group level status for a bulk file where some transactions passed and others did not.',
    severity: 'pending',
    action: 'Iterate the transaction level statuses. The group status is not enough.',
  },

  // ── ISO 20022 status reason codes ───────────────────────────────────────
  {
    code: 'AC01',
    family: 'iso-status-reason',
    name: 'IncorrectAccountNumber',
    description: 'The account identifier is wrong. The IBAN checksum passed but no such account exists at that bank.',
    severity: 'error',
    action: 'Data fix required. Retrying unchanged is guaranteed to fail again.',
  },
  {
    code: 'AC04',
    family: 'iso-status-reason',
    name: 'ClosedAccountNumber',
    description: 'The account existed but is closed.',
    severity: 'error',
    action: 'Ask the beneficiary for new details. Mark the stored IBAN dead.',
  },
  {
    code: 'AC06',
    family: 'iso-status-reason',
    name: 'BlockedAccount',
    description: 'Account blocked for the requested transaction type. Often a legal freeze or a dormancy block.',
    severity: 'error',
    action: 'Do not retry. Escalate to the beneficiary; a bank will not explain the block to you.',
  },
  {
    code: 'AG01',
    family: 'iso-status-reason',
    name: 'TransactionForbidden',
    description: 'The transaction type is not permitted on this account — a savings account refusing outbound transfers, typically.',
    severity: 'error',
    action: 'Offer the PSU a different debtor account.',
  },
  {
    code: 'AG02',
    family: 'iso-status-reason',
    name: 'InvalidBankOperationCode',
    description: 'The operation or service level code is wrong or unsupported by the receiving bank.',
    severity: 'error',
    action: 'Check SvcLvl and the local clearing rules. Usually a configuration bug on your side.',
  },
  {
    code: 'AM04',
    family: 'iso-status-reason',
    name: 'InsufficientFunds',
    description: 'Not enough money on the debtor account.',
    severity: 'error',
    action: 'A retry can legitimately succeed later. Back off and use the funds-confirmation endpoint first where available.',
  },
  {
    code: 'AM05',
    family: 'iso-status-reason',
    name: 'Duplication',
    description: 'The receiving bank recognises this as a duplicate of a payment it already processed.',
    severity: 'error',
    action: 'Do not resend. Check whether your idempotency handling is broken.',
  },
  {
    code: 'AM18',
    family: 'iso-status-reason',
    name: 'InvalidNumberOfTransactions',
    description: 'NbOfTxs does not match the transactions present in the message.',
    severity: 'error',
    action: 'Bulk file assembly bug. Recount and check CtrlSum too.',
  },
  {
    code: 'BE04',
    family: 'iso-status-reason',
    name: 'MissingCreditorAddress',
    description: 'Creditor address missing where the corridor requires it — cross-border and some regulated payment types.',
    severity: 'error',
    action: 'Add structured PstlAdr. Required more often since the CBPR+ migration.',
  },
  {
    code: 'BE05',
    family: 'iso-status-reason',
    name: 'UnrecognisedInitiatingParty',
    description: 'The initiating party is unknown to the receiving bank.',
    severity: 'error',
    action: 'Check your InitgPty organisation id against what the bank registered at onboarding.',
  },
  {
    code: 'CNOR',
    family: 'iso-status-reason',
    name: 'CreditorBankNotRegistered',
    description: 'The creditor bank is not reachable in the scheme you routed through.',
    severity: 'error',
    action: 'Check scheme reachability before routing. Common when sending SCT Inst to a bank that only supports SCT.',
  },
  {
    code: 'DT01',
    family: 'iso-status-reason',
    name: 'InvalidDate',
    description: 'The requested execution or settlement date is invalid — a weekend, a holiday, or in the past.',
    severity: 'error',
    action: 'Use a business-day calendar for the target scheme, not the local one.',
  },
  {
    code: 'FF01',
    family: 'iso-status-reason',
    name: 'InvalidFileFormat',
    description: 'The message does not conform to the expected format or usage guideline.',
    severity: 'error',
    action: 'Validate against the scheme guideline, not just the base XSD. SEPA restricts many optional elements.',
  },
  {
    code: 'MS02',
    family: 'iso-status-reason',
    name: 'NotSpecifiedReasonCustomerGenerated',
    description: 'The customer refused and gave no reason.',
    severity: 'error',
    action: 'Terminal. Nothing to diagnose.',
  },
  {
    code: 'MS03',
    family: 'iso-status-reason',
    name: 'NotSpecifiedReasonAgentGenerated',
    description: 'The bank refused and will not say why. Frequently a fraud or sanctions decision that cannot legally be disclosed.',
    severity: 'error',
    action: 'Stop guessing and stop retrying. Only the PSU, talking to their bank, can unblock this.',
  },
  {
    code: 'RC01',
    family: 'iso-status-reason',
    name: 'BankIdentifierIncorrect',
    description: 'The BIC is structurally invalid or unknown.',
    severity: 'error',
    action: 'Derive the BIC from the IBAN via a maintained directory instead of storing it.',
  },
  {
    code: 'RR04',
    family: 'iso-status-reason',
    name: 'RegulatoryReason',
    description: 'Refused for a regulatory reason — sanctions screening, embargo, or a reporting requirement not met.',
    severity: 'error',
    action: 'Never automate a retry. Route to compliance.',
  },
  {
    code: 'FOCR',
    family: 'iso-status-reason',
    name: 'FollowingCancellationRequest',
    description: 'Return executed because a recall was accepted.',
    severity: 'info',
    action: 'Match the incoming pacs.004 back to your camt.056 case id.',
  },
  {
    code: 'DUPL',
    family: 'iso-status-reason',
    name: 'DuplicatePayment',
    description: 'Recall reason: the same payment was sent twice.',
    severity: 'info',
    action: 'Raise within 10 business days for the SEPA technical-duplicate window.',
  },
  {
    code: 'FRAD',
    family: 'iso-status-reason',
    name: 'FraudulentOriginated',
    description: 'Recall reason: the payment originated from fraud.',
    severity: 'error',
    action: 'Time-critical. File immediately and in parallel with your own fraud process.',
  },
  {
    code: 'TECH',
    family: 'iso-status-reason',
    name: 'TechnicalProblem',
    description: 'Recall reason: a technical fault caused the erroneous payment.',
    severity: 'info',
    action: 'Include enough detail in AddtlInf for a human at the other bank to agree quickly.',
  },
  {
    code: 'CUST',
    family: 'iso-status-reason',
    name: 'RequestedByCustomer',
    description: 'Recall reason: the debtor asked for the payment back.',
    severity: 'info',
    action: 'The weakest ground of the four. Expect the creditor bank to ask its customer and often to refuse.',
  },
  {
    code: 'ACCR',
    family: 'iso-status-reason',
    name: 'CancellationAccepted',
    description: 'Recall accepted. Reported in camt.029 Sts/Conf, followed by a pacs.004 carrying the funds.',
    severity: 'success',
    action: 'Wait for the pacs.004. The camt.029 alone moves nothing.',
  },
  {
    code: 'ARDT',
    family: 'iso-status-reason',
    name: 'AlreadyReturned',
    description: 'Recall refused: the transaction has already been returned.',
    severity: 'error',
    action: 'Reconcile — the funds are probably already back and unmatched.',
  },
  {
    code: 'NOAS',
    family: 'iso-status-reason',
    name: 'NoAnswerFromCustomer',
    description: 'Recall refused: the beneficiary did not respond within the deadline.',
    severity: 'error',
    action: 'Recall route exhausted. Legal or scheme escalation is what remains.',
  },
  {
    code: 'NOOR',
    family: 'iso-status-reason',
    name: 'NoOriginalTransactionReceived',
    description: 'Recall refused: the creditor bank has no record of the original payment.',
    severity: 'error',
    action: 'Check the references you quoted. Usually the wrong OrgnlTxId.',
  },
  {
    code: 'LEGL',
    family: 'iso-status-reason',
    name: 'LegalDecision',
    description: 'Recall refused for legal reasons.',
    severity: 'error',
    action: 'Terminal for the recall channel.',
  },
  {
    code: 'MTCH',
    family: 'iso-status-reason',
    name: 'Match',
    description: 'Verification of Payee: the name matches the account holder.',
    severity: 'success',
    action: 'Proceed normally.',
  },
  {
    code: 'CMTC',
    family: 'iso-status-reason',
    name: 'CloseMatch',
    description: 'Verification of Payee: close match, with the correct name returned for the PSU to confirm.',
    severity: 'pending',
    action: 'Show the suggested name. Let the PSU choose; do not silently substitute it.',
  },
  {
    code: 'NMTC',
    family: 'iso-status-reason',
    name: 'NoMatch',
    description: 'Verification of Payee: the name does not match.',
    severity: 'error',
    action: 'Warn explicitly and log the PSU override. Liability may shift to whoever proceeded.',
  },

  // ── Berlin Group API errors ─────────────────────────────────────────────
  {
    code: 'CONSENT_INVALID',
    family: 'bg-error',
    name: 'Consent invalid',
    description: 'The consent does not cover this resource, or is no longer usable for it.',
    severity: 'error',
    http: 401,
    action: 'Check the access object you sent. Asking for balances under an availableAccounts consent lands here.',
  },
  {
    code: 'CONSENT_EXPIRED',
    family: 'bg-error',
    name: 'Consent expired',
    description: 'validUntil has passed, or the reauthentication interval elapsed.',
    severity: 'error',
    http: 401,
    action: 'Start a fresh consent. There is no renewal endpoint.',
  },
  {
    code: 'CONSENT_UNKNOWN',
    family: 'bg-error',
    name: 'Consent unknown',
    description: 'No consent with that id at this ASPSP.',
    severity: 'error',
    http: 403,
    action: 'Usually a sandbox-versus-production mix-up, or a consent that was purged.',
  },
  {
    code: 'ACCESS_EXCEEDED',
    family: 'bg-error',
    name: 'Access exceeded',
    description: 'frequencyPerDay used up for the day on an unattended access.',
    severity: 'error',
    http: 429,
    action: 'Budget your polls. PSU-present calls are exempt if you set the PSU-IP-Address header.',
  },
  {
    code: 'TOKEN_INVALID',
    family: 'bg-error',
    name: 'Token invalid',
    description: 'The OAuth2 token is malformed, revoked or not bound to this consent.',
    severity: 'error',
    http: 401,
    action: 'Refresh. If refresh fails, re-run SCA.',
  },
  {
    code: 'TOKEN_EXPIRED',
    family: 'bg-error',
    name: 'Token expired',
    description: 'Access token past its lifetime.',
    severity: 'error',
    http: 401,
    action: 'Use the refresh token. Do not re-run SCA for this alone.',
  },
  {
    code: 'FORMAT_ERROR',
    family: 'bg-error',
    name: 'Format error',
    description: 'A field is missing or malformed. The path member points at it.',
    severity: 'error',
    http: 400,
    action: 'Read tppMessages[].path. Amount-as-number is the classic cause.',
  },
  {
    code: 'PARAMETER_NOT_SUPPORTED',
    family: 'bg-error',
    name: 'Parameter not supported',
    description: 'A valid parameter that this ASPSP has not implemented.',
    severity: 'error',
    http: 400,
    action: 'Capability differences are normal. Keep a per-ASPSP feature matrix.',
  },
  {
    code: 'PRODUCT_INVALID',
    family: 'bg-error',
    name: 'Product invalid',
    description: 'The payment-product path segment is not offered on this account.',
    severity: 'error',
    http: 403,
    action: 'instant-sepa-credit-transfers is not universally reachable. Fall back to sepa-credit-transfers.',
  },
  {
    code: 'PERIOD_INVALID',
    family: 'bg-error',
    name: 'Period invalid',
    description: 'The dateFrom/dateTo range is outside what the ASPSP allows.',
    severity: 'error',
    http: 400,
    action: 'Many banks cap history at 90 days for unattended access. Page backwards in windows.',
  },
  {
    code: 'SCA_METHOD_UNKNOWN',
    family: 'bg-error',
    name: 'SCA method unknown',
    description: 'The authenticationMethodId you selected is not one the ASPSP offered.',
    severity: 'error',
    http: 400,
    action: 'Only ever echo ids from scaMethods in the previous response.',
  },
  {
    code: 'SERVICE_BLOCKED',
    family: 'bg-error',
    name: 'Service blocked',
    description: 'The PSU or the ASPSP has blocked this service for this TPP.',
    severity: 'error',
    http: 403,
    action: 'Terminal until the PSU acts. Not retryable.',
  },
  {
    code: 'CERTIFICATE_EXPIRED',
    family: 'bg-error',
    name: 'Certificate expired',
    description: 'Your QWAC or QSealC has expired.',
    severity: 'error',
    http: 401,
    action: 'Monitor expiry. This takes down every ASPSP at once and is entirely preventable.',
  },
  {
    code: 'SIGNATURE_MISSING',
    family: 'bg-error',
    name: 'Signature missing',
    description: 'This ASPSP requires an HTTP Signature and none was present.',
    severity: 'error',
    http: 401,
    action: 'Signing is per-ASPSP optional in the spec. Make it configurable, not compile-time.',
  },
  {
    code: 'NO_PIIS_ACTIVATION',
    family: 'bg-error',
    name: 'No PIIS activation',
    description: 'No funds-confirmation consent exists for this account. PIIS consent is established outside the API, usually at card issuance.',
    severity: 'error',
    http: 400,
    action: 'Nothing to retry. The PSU has to activate the service with the ASPSP first.',
  },
  {
    code: 'CARD_INVALID',
    family: 'bg-error',
    name: 'Card invalid',
    description: 'The card number is unknown, expired or does not belong to the account given.',
    severity: 'error',
    http: 400,
    action: 'Check the masked card number matches the instrument the PIIS consent covers.',
  },
  {
    code: 'RESOURCE_UNKNOWN',
    family: 'bg-error',
    name: 'Resource unknown',
    description: 'The path references a resource that does not exist for this consent.',
    severity: 'error',
    http: 404,
    action: 'resourceIds are consent-scoped. Re-list accounts after any new consent.',
  },

  // ── STET errors ─────────────────────────────────────────────────────────
  {
    code: 'invalid_client',
    family: 'stet-error',
    name: 'Invalid client',
    description: 'OAuth2 client authentication failed — unknown client or a certificate that does not match.',
    severity: 'error',
    http: 401,
    action: 'Confirm the QWAC presented on the TLS handshake is the one registered for that client_id.',
  },
  {
    code: 'invalid_scope',
    family: 'stet-error',
    name: 'Invalid scope',
    description: 'Requested scope not granted to this TPP role.',
    severity: 'error',
    http: 400,
    action: 'aisp and pisp are separate. Your eIDAS role attributes must cover the scope.',
  },
  {
    code: 'signature_invalid',
    family: 'stet-error',
    name: 'HTTP Signature invalid',
    description: 'The Signature header does not verify against the digest and headers listed.',
    severity: 'error',
    http: 401,
    action: 'Sign the exact bytes you send. Any re-serialisation between signing and sending breaks this.',
  },
  {
    code: 'NOK',
    family: 'stet-error',
    name: 'Rejected',
    description: 'STET generic rejection at resource level.',
    severity: 'error',
    http: 400,
    action: 'Read the resource body — the useful detail is inside, not in the code.',
  },

  // ── UK Open Banking errors ──────────────────────────────────────────────
  {
    code: 'UK.OBIE.Field.Missing',
    family: 'ukob-error',
    name: 'Field missing',
    description: 'A required field is absent. The Path member locates it precisely.',
    severity: 'error',
    http: 400,
    action: 'OBIE error bodies are the most precise of any standard. Log Path and fix exactly that.',
  },
  {
    code: 'UK.OBIE.Field.Invalid',
    family: 'ukob-error',
    name: 'Field invalid',
    description: 'Present but fails validation — wrong format, length or enum value.',
    severity: 'error',
    http: 400,
    action: 'Check the field against the OBIE data dictionary, not just the JSON schema.',
  },
  {
    code: 'UK.OBIE.Signature.Invalid',
    family: 'ukob-error',
    name: 'Detached JWS invalid',
    description: 'x-jws-signature does not verify.',
    severity: 'error',
    http: 401,
    action: 'b64 must be false, and the critical header claims must be exactly the OB set. Sign the raw body bytes.',
  },
  {
    code: 'UK.OBIE.Resource.InvalidConsentStatus',
    family: 'ukob-error',
    name: 'Invalid consent status',
    description: 'The consent is not in a state that permits this operation.',
    severity: 'error',
    http: 400,
    action: 'A funds-confirmation before authorisation lands here. Check consent status first.',
  },
  {
    code: 'UK.OBIE.Rules.FailsControlParameters',
    family: 'ukob-error',
    name: 'Fails control parameters',
    description: 'A VRP payment breaches the mandate limits the PSU authorised.',
    severity: 'error',
    http: 400,
    action: 'Track remaining headroom client-side. Never rely on the bank to be your limit checker.',
  },
  {
    code: 'UK.OBIE.Reauthenticate',
    family: 'ukob-error',
    name: 'Reauthenticate',
    description: 'SCA is required again before this call can succeed.',
    severity: 'error',
    http: 403,
    action: 'Send the PSU back through authorisation. Preserve their basket state first.',
  },
  {
    code: 'UK.OBIE.Unsupported.Scheme',
    family: 'ukob-error',
    name: 'Unsupported scheme',
    description: 'The account identification SchemeName is not supported for this operation.',
    severity: 'error',
    http: 400,
    action: 'Domestic payments need UK.OBIE.SortCodeAccountNumber; IBAN is for international.',
  },

  // ── SCA and consent lifecycle ───────────────────────────────────────────
  {
    code: 'received',
    family: 'sca-status',
    name: 'Received',
    description: 'Authorisation sub-resource created; nothing has happened yet.',
    severity: 'info',
    action: 'Select an SCA method or redirect the PSU.',
  },
  {
    code: 'psuIdentified',
    family: 'sca-status',
    name: 'PSU identified',
    description: 'The PSU is known but has not authenticated.',
    severity: 'info',
    action: 'Continue to method selection.',
  },
  {
    code: 'scaMethodSelected',
    family: 'sca-status',
    name: 'SCA method selected',
    description: 'A method was chosen and the challenge is being prepared.',
    severity: 'pending',
    action: 'Wait for started, then poll.',
  },
  {
    code: 'started',
    family: 'sca-status',
    name: 'Started',
    description: 'The challenge has been delivered to the PSU.',
    severity: 'pending',
    action: 'Poll with backoff and set a deadline. Abandonment is the most common outcome.',
  },
  {
    code: 'finalised',
    family: 'sca-status',
    name: 'Finalised',
    description: 'SCA completed successfully.',
    severity: 'success',
    action: 'Proceed to the resource, or to the confirmation call under STET.',
  },
  {
    code: 'failed',
    family: 'sca-status',
    name: 'Failed',
    description: 'SCA failed or was abandoned.',
    severity: 'error',
    action: 'Terminal for this authorisation. A new one may be permitted; the resource may not be.',
  },
  {
    code: 'exempted',
    family: 'sca-status',
    name: 'Exempted',
    description: 'SCA was not required — a trusted beneficiary or a low-value exemption applied.',
    severity: 'success',
    action: 'Proceed. Do not assume the exemption repeats.',
  },
  {
    code: 'valid',
    family: 'consent-status',
    name: 'Valid',
    description: 'Consent authorised and usable.',
    severity: 'success',
    action: 'Read data. Watch validUntil and your frequency budget.',
  },
  {
    code: 'revokedByPsu',
    family: 'consent-status',
    name: 'Revoked by PSU',
    description: 'The PSU withdrew consent, usually in the bank app.',
    severity: 'error',
    action: 'Delete the cached data your agreement says you must, and stop calling.',
  },
  {
    code: 'expired',
    family: 'consent-status',
    name: 'Expired',
    description: 'Past validUntil.',
    severity: 'error',
    action: 'New consent, new SCA.',
  },
  {
    code: 'terminatedByTpp',
    family: 'consent-status',
    name: 'Terminated by TPP',
    description: 'You called DELETE on the consent.',
    severity: 'info',
    action: 'Do this when a user disconnects an account. It is good hygiene and some regulators check.',
  },
  {
    code: 'CANCELLATION_INVALID',
    family: 'bg-error',
    name: 'Cancellation invalid',
    description: 'DELETE was refused — payment already executed, cancelled, or past the cancellable window.',
    severity: 'error',
    http: 400,
    action: 'Read status. If ACSC/ACSP, use recall (camt.056), not another DELETE.',
  },
  {
    code: 'WERO_CREATED',
    family: 'scheme-status',
    name: 'Wero payment created',
    description: 'Payment intent accepted by the scheme; not yet authorised or settled.',
    severity: 'pending',
    action: 'Continue to proxy resolve and ASPSP auth. Cancellable here.',
  },
  {
    code: 'WERO_PROXY_FOUND',
    family: 'scheme-status',
    name: 'Proxy found',
    description: 'Alias resolved to an IBAN at a participating ASPSP.',
    severity: 'success',
    action: 'Show display name; run VoP if required before debit.',
  },
  {
    code: 'WERO_PROXY_NOT_FOUND',
    family: 'scheme-status',
    name: 'Proxy not found',
    description: 'Alias is not registered in the Wero directory.',
    severity: 'error',
    action: 'Ask for IBAN or another alias. Do not invent a fallback transfer.',
  },
  {
    code: 'WERO_AUTH_REQUIRED',
    family: 'scheme-status',
    name: 'ASPSP auth required',
    description: 'Scheme is waiting for PSU authentication at the debtor ASPSP.',
    severity: 'pending',
    action: 'Keep the intent open; handle abandon with cancel.',
  },
  {
    code: 'WERO_AUTHORIZED',
    family: 'scheme-status',
    name: 'Authorised',
    description: 'PSU authorised the debit; SCT Inst submission can proceed.',
    severity: 'pending',
    action: 'Await clearing ACSC before showing paid.',
  },
  {
    code: 'WERO_COMPLETED',
    family: 'scheme-status',
    name: 'Completed',
    description: 'Scheme terminal success after instant settlement confirmation.',
    severity: 'success',
    action: 'Release goods / confirm P2P. Reconcile on EndToEndId.',
  },
  {
    code: 'AB05',
    family: 'iso-status-reason',
    name: 'Timeout creditor agent',
    description: 'Creditor PSP did not respond in time, or is not reachable for this product (often SCT Inst reachability).',
    severity: 'error',
    action: 'Do not blind-retry INST. Check SCT Inst adherence; offer standard SCT with PSU consent.',
  },
  {
    code: 'AB06',
    family: 'iso-status-reason',
    name: 'Timeout instructing agent',
    description: 'Timeout at an instructing agent in the chain.',
    severity: 'error',
    action: 'Investigate with pacs.028; treat like other instant timeouts until resolved.',
  },
  {
    code: 'AB07',
    family: 'iso-status-reason',
    name: 'Offline agent',
    description: 'Agent offline — common around CSM maintenance windows.',
    severity: 'error',
    action: 'Retry after the published maintenance window; inform the PSU of planned downtime when known.',
  },
  {
    code: 'AB08',
    family: 'iso-status-reason',
    name: 'Offline creditor agent',
    description: 'Creditor PSP offline for instant processing.',
    severity: 'error',
    action: 'Same as AB05/AB07 — do not leave the reservation open forever.',
  },
  {
    code: 'AB09',
    family: 'iso-status-reason',
    name: 'Error creditor agent',
    description: 'Creditor agent processing error on an instant transfer.',
    severity: 'error',
    action: 'Read accompanying information; may be transient — one careful retry max after investigation.',
  },
  {
    code: 'AC03',
    family: 'iso-status-reason',
    name: 'Invalid creditor account number',
    description: 'Creditor account number invalid (distinct from AC01 IBAN check-digit failures in some markets).',
    severity: 'error',
    action: 'Fix account identification with the PSU. Do not retry the same values.',
  },
  {
    code: 'TM01',
    family: 'iso-status-reason',
    name: 'Invalid Cut Off Time / timeout',
    description: 'Used in instant contexts when the transaction misses the processing window.',
    severity: 'error',
    action: 'Hold reservation and investigate; never interpret as a confirmed failure without pacs.002 or rulebook timeout expiry.',
  },
  {
    code: 'AM02',
    family: 'iso-status-reason',
    name: 'Not Allowed Amount',
    description: 'Amount exceeds scheme or ASPSP maximum for SCT Inst.',
    severity: 'error',
    action: 'Split the payment or use standard SCT. Check current scheme max amount.',
  },
  {
    code: 'PAYMENT_FAILED',
    family: 'bg-error',
    name: 'Payment failed',
    description: 'Generic Berlin Group failure on a payment resource.',
    severity: 'error',
    http: 400,
    action: 'Read tppMessages and transactionStatus. For instant, correlate with pacs.002 reason codes.',
  },
];
